/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import { ComponentManagerFn } from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { baseColumnProps, isBaseColumnPropertyVisible } from '../BaseColumn';
import { defaultValue } from './RowNumberColumn';

const RowNumberColumnManager: ComponentManagerFn<'RowNumberColumn'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'RowNumberColumn'>(platform, 'RowNumberColumn'),
    type: 'RowNumberColumn',
    name: 'Row Number Column',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnCellClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...baseColumnProps,
      viewAttribute: {
        ...baseColumnProps.viewAttribute,
        required: false,
      },
    },
    isPropertyVisible(prop, itemData, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }
      switch (prop) {
        case 'viewAttribute':
        case 'insertable':
        case 'updatable':
        case 'filterable':
        case 'sortable':
        case 'required':
        case 'flex':
        case 'toolTip':
        case 'toolTipPosition':
        case 'toolTipMaxWidth':
          return false;
        default:
          return true;
      }
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    category: 'Data Grid',
    icon: { iconName: 'io-row-number', iconType: 'cloudio' },
    description: `**Overview**<br/>
The Row Number Column is a utility column that automatically displays the sequential index number (1, 2, 3...) for each row in a Data Grid.
Unlike a database ID (which might be 1045 and non-sequential), this column always starts at 1 for the first visible row. It provides a stable, easy way for users to reference specific records during discussions (e.g., "Look at the error in Row 5").

**1. Behavior & Configuration**<br/>
This column is essentially "Zero Configuration" because it does not require a data source.
* **No Data Binding:** It does not bind to any attribute in your Data Store. The grid generates the numbers automatically at runtime.
* **Read-Only:** Since the values are auto-generated indices, this column is strictly read-only. Properties like Insertable, Updatable, Sortable, and Filterable are automatically hidden/disabled to prevent misconfiguration.

**2. Interaction**<br/>
While primarily visual, the column can be interactive.
* **TriggerOnCellClick:** You can add this child component to execute logic when the user clicks the row number itself. (Use Case: Some interfaces use the row number click as a way to "Select" the row or open a "Row Context Menu").

**3. Usage Best Practices**<br/>
* **Placement:** By convention, this is almost always placed as the first column on the left side of the grid.
* **Vs. Database ID:** Do not confuse this with your primary key. If you re-sort the grid, "Row 1" will be a different record. If you need to see the actual Record ID, use a standard Number Column or String Column bound to your ID attribute instead.`,
  };
};

export default RowNumberColumnManager;
